import React, { useState, useRef, useEffect } from "react";
import { X, Check, ZoomIn, ZoomOut, RotateCw } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion, useMotionValue, useTransform, useAnimation } from "framer-motion";
import { useI18n } from "@/lib/i18n";

interface ImageCropperModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    image: string; // Base64
    onCrop: (croppedImage: string) => void;
    aspectRatio?: number; // default 1 (square)
}

export function ImageCropperModal({
    open,
    onOpenChange,
    image,
    onCrop,
    aspectRatio = 1,
}: ImageCropperModalProps) {
    const { t } = useI18n();
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLImageElement>(null);

    // Motion values for pan
    const x = useMotionValue(0);
    const y = useMotionValue(0);

    useEffect(() => {
        if (open) {
            setScale(1);
            setRotation(0);
            x.set(0);
            y.set(0);
        }
    }, [open, image, x, y]);

    const handleCrop = () => {
        if (!imageRef.current) return;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Output size (scaled by aspect ratio)
        const baseWidth = 1024;
        canvas.width = baseWidth;
        canvas.height = baseWidth / aspectRatio;

        const img = imageRef.current;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        // Logic to calculate crop from scale, pan, and rotation
        // For now, let's implement a simplified version that handles basic scale and pan

        // 1. Calculate the image's displayed size
        const containerSize = Math.min(rect.width, rect.height) - 40; // Subtract padding

        // 2. Draw 
        ctx.save();
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((rotation * Math.PI) / 180);

        const scaleFactor = scale * (canvas.width / containerSize);

        // The relative offset from the center of the aperture
        const relativeX = x.get() * (canvas.width / containerSize);
        const relativeY = y.get() * (canvas.width / containerSize);

        // Draw image centered at the aperture center + offset
        // The image's intrinsic size is img.naturalWidth x img.naturalHeight
        // We want its width to be (mapped width * scaleFactor)

        // We need to know how the image was initially fitted in the container
        const imgAspect = img.naturalWidth / img.naturalHeight;
        let initialWidth, initialHeight;

        if (imgAspect > aspectRatio) {
            initialHeight = containerSize / aspectRatio;
            initialWidth = initialHeight * imgAspect;
        } else {
            initialWidth = containerSize;
            initialHeight = containerSize / imgAspect;
        }

        const drawWidth = initialWidth * (canvas.width / containerSize) * scale;
        const drawHeight = initialHeight * (canvas.width / containerSize) * scale;

        ctx.drawImage(
            img,
            -drawWidth / 2 + relativeX,
            -drawHeight / 2 + relativeY,
            drawWidth,
            drawHeight
        );

        ctx.restore();

        const croppedBase64 = canvas.toDataURL("image/jpeg", 0.9);
        onCrop(croppedBase64);
        onOpenChange(false);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md p-0 overflow-hidden bg-black flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh] border-none shadow-2xl">
                <div className="flex-none flex items-center justify-between p-4 text-white bg-black/50 backdrop-blur-md z-10 border-b border-white/10">
                    <DialogTitle className="text-sm font-semibold tracking-tight">
                        {t("postComposer.cropImage") || "Afbeelding Snijden"}
                    </DialogTitle>
                    <button onClick={() => onOpenChange(false)} className="hover:opacity-70 transition-opacity">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="flex-1 min-h-0 relative flex items-center justify-center bg-[#111] overflow-hidden p-10" ref={containerRef}>
                    {/* Aperture Overlay */}
                    <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center">
                        <div
                            className="w-[calc(100%-40px)] border-2 border-magenta/50 shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] rounded-sm"
                            style={{ aspectRatio: aspectRatio }}
                        />
                    </div>

                    <motion.img
                        ref={imageRef}
                        src={image.startsWith("data:") ? image : `data:image/jpeg;base64,${image}`}
                        style={{ x, y, scale, rotate: rotation }}
                        drag
                        dragElastic={0.1}
                        dragConstraints={false}
                        className="max-w-none cursor-move select-none"
                        onDragStart={(e) => e.preventDefault()}
                    />
                </div>

                <div className="flex-none p-4 sm:p-6 space-y-4 bg-[#111] border-t border-white/10">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <input
                                type="range"
                                min="1"
                                max="3"
                                step="0.01"
                                value={scale}
                                onChange={(e) => setScale(parseFloat(e.target.value))}
                                className="w-24 accent-magenta"
                            />
                            <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-white/20 hover:bg-white/10 text-white" onClick={() => setScale(s => Math.min(3, s + 0.1))}>
                                <ZoomIn className="h-4 w-4" />
                            </Button>
                        </div>

                        <Button variant="outline" size="icon" className="h-8 w-8 rounded-full border-white/20 hover:bg-white/10 text-white" onClick={() => setRotation(r => r + 90)}>
                            <RotateCw className="h-4 w-4" />
                        </Button>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button variant="outline" className="flex-1 bg-transparent text-white border-white/20 hover:bg-white/5" onClick={() => onOpenChange(false)}>
                            {t("common.cancel")}
                        </Button>
                        <Button className="flex-1 bg-magenta hover:bg-magenta-hover text-white font-bold" onClick={handleCrop}>
                            <Check className="h-4 w-4 mr-2" />
                            {t("common.save") || "Opslaan"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
